import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Loader2, ArrowRight } from 'lucide-react';

/**
 * Modal shown when an active subscriber clicks "Update Plan".
 * Renders a from→to summary, line-item diff, proration notice, and
 * confirm/cancel buttons.
 */
export default function ConfirmModifyDialog({
  open, onOpenChange, sub, estimate, stripeDetails, addonChanges,
  modifyLoading, handleModify,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="confirm-modify-dialog">
        <DialogHeader>
          <DialogTitle>Confirm Plan Changes</DialogTitle>
          <DialogDescription>Review the changes before updating your subscription.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Monthly plan from → to */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Monthly Plan</p>
            <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-3">
              <div className="text-center">
                <p className="text-xs text-gray-500">Current</p>
                <p className="text-lg font-bold text-gray-800">${sub.billing?.total_monthly?.toFixed(2)}</p>
              </div>
              <ArrowRight className="w-4 h-4 text-gray-400 shrink-0" />
              <div className="text-center">
                <p className="text-xs text-gray-500">New</p>
                <p className="text-lg font-bold text-teal-600">${estimate?.total_monthly?.toFixed(2)}</p>
              </div>
              <div className="ml-auto text-right">
                <p className="text-xs text-gray-500">Difference</p>
                {estimate && sub.billing && (() => {
                  const diff = estimate.total_monthly - sub.billing.total_monthly;
                  return (
                    <p className={`text-sm font-semibold ${diff > 0 ? 'text-amber-600' : diff < 0 ? 'text-green-600' : 'text-gray-500'}`}>
                      {diff > 0 ? '+' : ''}{diff === 0 ? 'No change' : `$${diff.toFixed(2)}/mo`}
                    </p>
                  );
                })()}
              </div>
            </div>
          </div>

          {/* What changed */}
          {addonChanges.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">What Changed</p>
              <div className="space-y-1.5">
                {addonChanges.map((c) => (
                  <div key={c.label} className="flex items-center justify-between text-sm bg-gray-50 rounded px-3 py-2">
                    <span className="text-gray-700">
                      <span className={`font-semibold ${c.diff > 0 ? 'text-teal-600' : 'text-red-500'}`}>
                        {c.diff > 0 ? `+${c.diff}` : c.diff}
                      </span>
                      {' '}{c.label}
                    </span>
                    <span className={`font-medium ${c.costDiff > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                      {c.costDiff > 0 ? '+' : ''}{c.costDiff === 0 ? '$0.00' : `$${c.costDiff.toFixed(2)}`}/mo
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Proration info */}
          <div className="bg-blue-50 border border-blue-100 rounded-lg px-4 py-3">
            <p className="text-sm text-blue-800">
              {estimate && sub.billing && estimate.total_monthly > sub.billing.total_monthly
                ? <>A prorated charge will be added to your <span className="font-semibold">next invoice</span>{stripeDetails.current_period_end ? ` (due ${new Date(stripeDetails.current_period_end).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })})` : ''}.</>
                : estimate && sub.billing && estimate.total_monthly < sub.billing.total_monthly
                  ? <>A prorated credit will be applied to your <span className="font-semibold">next invoice</span>{stripeDetails.current_period_end ? ` (due ${new Date(stripeDetails.current_period_end).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })})` : ''}.</>
                  : <>No billing changes — your next invoice amount stays the same.</>
              }
            </p>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={modifyLoading} data-testid="confirm-modify-cancel">
            Cancel
          </Button>
          <Button
            onClick={handleModify}
            className="bg-teal-500 hover:bg-teal-600 text-white"
            disabled={modifyLoading}
            data-testid="confirm-modify-submit"
          >
            {modifyLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Updating...</> : 'Confirm Update'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
